
import React, { useState, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'
import { getPowerplants, getPowerplantData } from 'utils/API/powerplants'
import { dataToMWh } from 'utils/data'

const APIContext = React.createContext({})

export default APIContext

const addDate = (apiData) => ({
  ...apiData,
  date: moment.utc(`${apiData.year + apiData.month + apiData.day}`, 'YYYYMMDD'),
})

const useCentrals = () => {
  const [centralsIds, setCentralsIds] = useState([])
  const [centralsMetaData, setCentralsMetadata] = useState({})
  useEffect(() => {
    const getCentrals = async () => {
      const centrals = await getPowerplants()
      setCentralsIds(centrals.map(({ id }) => id))
      setCentralsMetadata(centrals.reduce((p, n) => {
        const {
          day_soiling: daySoiling = 0,
          fancy_name: fancyName = '',
          nominal_power: nominalPower = 0,
          time_resolution: timeResolution = 0,
        } = n
        return {
          ...p,
          [n.id]: {
            ...n,
            daySoiling,
            fancyName,
            nominalPower,
            timeResolution,
          },
        }
      }, {}))
    }
    getCentrals()
  }, [])
  return { centralsIds, centralsMetaData }
}

const APIStore = ({ children }) => {
  const { centralsIds, centralsMetaData } = useCentrals()
  const [centralsData, setCentralsData] = useState({})

  const resetCleanings = useCallback(async (centralId) => {
    const cleanings = await getPowerplantData(centralId, { type: 'cleaning', resolution: 'day' })
    setCentralsData((prev) => ({
      ...prev,
      [centralId]: {
        ...prev[centralId],
        cleaning: cleanings.map(addDate),
      },
    }))
  }, [])
  useEffect(() => {
    async function getDataFromPowerplantAPI(centralId) {
      const [power, soiling, soilingPercent, cleaning = [],
        rain = [], soilingSensor = [], soilingSensor2 = []] = await Promise.all(
        ['power', 'soiling', 'soiling_percent', 'cleaning', 'rain', 'soiling_sensor', 'soiling_sensor2']
          .map((type) => getPowerplantData(centralId, { type, resolution: 'day' })),
      )
      return {
        power: dataToMWh(power.map(addDate)),
        soiling: dataToMWh(soiling.map(addDate)),
        soilingPercent: soilingPercent.map(addDate),
        soilingSensor: soilingSensor.map(addDate),
        soilingSensor2: soilingSensor2.map(addDate),
        cleaning: cleaning.map(addDate),
        rain: rain.map(addDate),
        dates: power.map(({ year, month, day }) => moment.utc(`${year + month + day}`, 'YYYYMMDD')),
        id: centralId,
      }
    }
    async function setDataFromPowerplantAPI() {
      const data = await Promise.all(
        centralsIds
          .map((centralId) => getDataFromPowerplantAPI(centralId)),
      )
      setCentralsData(centralsIds.reduce((prev, id, index) => ({
        ...prev,
        [id]: {
          ...data[index],
        },
      }), {}))
    }
    setDataFromPowerplantAPI()
  }, [centralsIds])
  return (
    <APIContext.Provider value={{
      centralsIds,
      centralsMetaData,
      centralsData,
      resetCleanings,
    }}
    >
      {children}
    </APIContext.Provider>
  )
}
APIStore.propTypes = { children: PropTypes.node.isRequired }
export { APIStore }
